import { useEffect } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { useStatsigExperiment } from '@src/experiments/statsig-integration'
import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'
import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { EDDProductType, EDDRequestProductType } from '@src/quince-site-common/src/services/eddService/eddService.types'

// eslint-disable-next-line import/no-named-as-default
import DeliveryDetailsByColorType from '../../../domains/ProductDeliveryDetails'
import usePrevious from '../../../hooks/usePrevious'
import { useZipCode } from '../../../hooks/zipcode'

import productDynamicDataStateManager from './ProductDynamicDataStateManager'

const useProductDynamicData = ({
  productId,
  productTags,
  isProductConstructedOnServer,
  skipTagRefresh = false,
  wrapperRef,
  eddRequestedProducts,
}: {
  productId: number | string
  productTags?: ProductTag[]
  isProductConstructedOnServer: boolean
  skipTagRefresh?: boolean
  wrapperRef?: React.RefObject<any>
  eddRequestedProducts?: EDDRequestProductType
}) => {
  const { zipCode } = useZipCode()
  const prevZipcode = usePrevious(zipCode)

  const dynamicProductTags = useAppSelector<ProductTag[] | undefined>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getProductTags,
    [productId, zipCode],
  )

  const dynamicProductDeliveryInfo = useAppSelector<DeliveryDetailsByColorType | undefined>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getProductDeliveryInfo,
    [productId, zipCode],
  )

  const newColorOrder = useAppSelector<ProductOptionValue[] | undefined>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getDynamicProductColorOrder,
    [productId, zipCode],
  )

  const colorOrder = useAppSelector<ProductOptionValue[] | undefined>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getProductColorOrder,
    [productId, zipCode],
  )

  const isWhiteGloveProduct = useAppSelector<boolean | undefined>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getIsWhiteGloveProduct,
    [productId, zipCode],
  )

  const productEDDDetails = useAppSelector<EDDProductType>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getProductEDD,
    [productId, zipCode],
  )

  const productReviewImages = useAppSelector<ProductReview[]>(
    productDynamicDataStateManager,
    productDynamicDataStateManager.getProductReviewImages,
    [productId],
  )

  const { experimentBucket: eddExpBucket, init } = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)

  useEffect(() => {
    if (init) {
      productDynamicDataStateManager.resolveEDDData(eddRequestedProducts, zipCode, wrapperRef)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, productId, zipCode, eddExpBucket, init])

  useEffect(() => {
    const zipcodeUpdated = prevZipcode !== zipCode
    if (isProductConstructedOnServer || zipcodeUpdated) {
      productDynamicDataStateManager.resolveDynamicData(productId, zipCode, zipcodeUpdated, skipTagRefresh)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, zipCode, skipTagRefresh, prevZipcode])

  return {
    productTags: dynamicProductTags ? dynamicProductTags : productTags,
    productEDDDetails,
    deliveryInfo: dynamicProductDeliveryInfo,
    newColorOrder,
    colorOrder,
    isWhiteGloveProduct,
    productReviewImages,
  }
}

export default useProductDynamicData
